// src/Marges.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import './styles/ManagementPages.css';

const AUTO_TYPE_LABELS = {
  0: 'Prix fixe',
  1: '% Intrinsèque',
  2: '% Aoea',
};

const TABS = [
  { id: 'devises', label: '💱 Devises' },
  { id: 'produits', label: '🪙 Produits' },
  { id: 'titrages', label: '⚖️ Or au carat' },
];

const formatEuro = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num) || num === 0) return '—';
  return num.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
};

const Marges = () => {
  const authToken = localStorage.getItem('token');

  const [agences, setAgences] = useState([]);
  const [idMag, setIdMag] = useState('');
  const [activeTab, setActiveTab] = useState('devises');

  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');
  const [search, setSearch] = useState('');

  // Catalogue produits de l'API (visuels + infos), indexé par id
  const [apiProducts, setApiProducts] = useState(null);
  const [onlyApi, setOnlyApi] = useState(false);

  // Édition inline
  const [editKey, setEditKey] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${authToken}` }),
    [authToken]
  );

  // Charger la liste des agences
  useEffect(() => {
    axios
      .get('/api/marges/agences', { headers })
      .then((res) => {
        const list = (res.data.data || []).filter((a) => a.label);
        setAgences(list);
      })
      .catch((err) => {
        setError(err.response?.data?.error || 'Erreur lors du chargement des agences');
      });
  }, [headers]);

  // Charger le catalogue produits API (visuels, badges) une seule fois
  useEffect(() => {
    axios
      .get('/api/products', { params: { catalogue: 'agences' }, headers })
      .then((res) => {
        const map = {};
        (res.data || []).forEach((p) => {
          map[p.id] = p;
        });
        setApiProducts(map);
      })
      .catch(() => setApiProducts({}));
  }, [headers]);

  // Charger les marges de l'onglet actif
  const fetchRows = useCallback(async () => {
    if (!idMag) {
      setRows([]);
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const res = await axios.get(`/api/marges/${activeTab}`, {
        params: { id_mag: idMag },
        headers,
      });
      setRows(res.data.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors du chargement des marges');
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  }, [idMag, activeTab, headers]);

  useEffect(() => {
    setEditKey(null);
    setSearch('');
    fetchRows();
  }, [fetchRows]);

  const startEdit = (key, values) => {
    setEditKey(key);
    setEditValues(values);
    setError('');
    setFeedback('');
  };

  const cancelEdit = () => {
    setEditKey(null);
    setEditValues({});
  };

  const setField = (field, value) => {
    setEditValues((prev) => ({ ...prev, [field]: value }));
  };

  const saveEdit = async (body, successMessage) => {
    setIsSaving(true);
    setError('');
    setFeedback('');
    try {
      await axios.put(`/api/marges/${activeTab}`, { id_mag: Number(idMag), ...body }, { headers });
      setFeedback(successMessage);
      cancelEdit();
      await fetchRows();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la mise à jour');
    } finally {
      setIsSaving(false);
    }
  };

  const filteredRows = useMemo(() => {
    let list = rows;
    const term = search.trim().toLowerCase();
    if (term) {
      list = list.filter((r) =>
        [r.label, r.code, r.label_fr]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(term))
      );
    }
    if (activeTab === 'produits' && apiProducts) {
      if (onlyApi) {
        list = list.filter((r) => apiProducts[r.id_achator]);
      }
      // Produits présents dans l'API en premier, puis ordre alphabétique
      list = [...list].sort((a, b) => {
        const aIn = apiProducts[a.id_achator] ? 0 : 1;
        const bIn = apiProducts[b.id_achator] ? 0 : 1;
        if (aIn !== bIn) return aIn - bIn;
        return String(a.label || '').localeCompare(String(b.label || ''), 'fr');
      });
    }
    return list;
  }, [rows, search, activeTab, apiProducts, onlyApi]);

  const apiProductsCount = useMemo(() => {
    if (!apiProducts) return 0;
    return rows.filter((r) => apiProducts[r.id_achator]).length;
  }, [rows, apiProducts]);

  const agenceLabel = useMemo(() => {
    const found = agences.find((a) => String(a.id) === String(idMag));
    return found ? found.label : '';
  }, [agences, idMag]);

  // ---------- Rendu des tableaux par onglet ----------

  const renderDevises = () => (
    <table className="management-table">
      <thead>
        <tr>
          <th>Code</th>
          <th>Devise</th>
          <th>Marge Achat %</th>
          <th>Marge Vente %</th>
          <th>Statut</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {filteredRows.map((row) => {
          const key = `dev-${row.id_currency}`;
          const isEditing = editKey === key;
          return (
            <tr key={key}>
              <td><strong>{row.code}</strong></td>
              <td>{row.label_fr}</td>
              <td>
                {isEditing ? (
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="mgmt-input mgmt-input--small"
                    value={editValues.valeurA}
                    onChange={(e) => setField('valeurA', e.target.value)}
                  />
                ) : (
                  `${row.valeurA} %`
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="mgmt-input mgmt-input--small"
                    value={editValues.valeurV}
                    onChange={(e) => setField('valeurV', e.target.value)}
                  />
                ) : (
                  `${row.valeurV} %`
                )}
              </td>
              <td>
                <span className={`management-tag ${row.status ? 'management-tag--success' : 'management-tag--warning'}`}>
                  {row.status ? 'Actif' : 'Inactif'}
                </span>
              </td>
              <td>
                {isEditing ? (
                  <div className="management-actions">
                    <button
                      type="button"
                      className="mgmt-btn mgmt-btn--primary mgmt-btn--small"
                      disabled={isSaving}
                      onClick={() =>
                        saveEdit(
                          {
                            id_currency: row.id_currency,
                            valeurA: Number(editValues.valeurA),
                            valeurV: Number(editValues.valeurV),
                          },
                          `Marges ${row.code} mises à jour pour ${agenceLabel}.`
                        )
                      }
                    >
                      {isSaving ? '…' : 'Enregistrer'}
                    </button>
                    <button type="button" className="mgmt-btn mgmt-btn--ghost mgmt-btn--small" onClick={cancelEdit}>
                      Annuler
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="mgmt-btn mgmt-btn--outline mgmt-btn--small"
                    onClick={() => startEdit(key, { valeurA: row.valeurA, valeurV: row.valeurV })}
                  >
                    Modifier
                  </button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  const renderProduits = () => (
    <table className="management-table">
      <thead>
        <tr>
          <th>Produit</th>
          <th>Mode Vente</th>
          <th>% Vente</th>
          <th>Mode Achat</th>
          <th>% Achat</th>
          <th>Prix V / A</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {filteredRows.map((row) => {
          const key = `prod-${row.id_achator}`;
          const isEditing = editKey === key;
          const apiInfo = apiProducts ? apiProducts[row.id_achator] : null;
          return (
            <tr key={key} className={apiInfo ? 'marges-row--api' : ''}>
              <td>
                <div className="marges-product-cell">
                  {apiInfo?.image_url ? (
                    <img
                      className="marges-product-thumb"
                      src={`${apiInfo.image_url}?quality=60`}
                      alt={row.label}
                      loading="lazy"
                    />
                  ) : (
                    <span className="marges-product-thumb marges-product-thumb--empty">🪙</span>
                  )}
                  <div>
                    <strong>{row.label}</strong>
                    <div className="marges-product-sub">
                      <span>#{row.id_achator}</span>
                      {apiInfo && <span className="management-tag management-tag--success">API</span>}
                      {apiInfo?.vente_seule && (
                        <span className="management-tag management-tag--warning">Vente seule</span>
                      )}
                    </div>
                  </div>
                </div>
              </td>
              <td>
                {isEditing ? (
                  <select
                    className="mgmt-input mgmt-input--small"
                    value={editValues.auto_typeV}
                    onChange={(e) => setField('auto_typeV', e.target.value)}
                  >
                    {Object.entries(AUTO_TYPE_LABELS).map(([v, l]) => (
                      <option key={v} value={v}>{l}</option>
                    ))}
                  </select>
                ) : (
                  <span className="management-tag">{AUTO_TYPE_LABELS[row.auto_typeV]}</span>
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="mgmt-input mgmt-input--small"
                    value={editValues.auto_pourcentV}
                    onChange={(e) => setField('auto_pourcentV', e.target.value)}
                  />
                ) : (
                  `${row.auto_pourcentV} %`
                )}
              </td>
              <td>
                {isEditing ? (
                  <select
                    className="mgmt-input mgmt-input--small"
                    value={editValues.auto_typeA}
                    onChange={(e) => setField('auto_typeA', e.target.value)}
                  >
                    {Object.entries(AUTO_TYPE_LABELS).map(([v, l]) => (
                      <option key={v} value={v}>{l}</option>
                    ))}
                  </select>
                ) : (
                  <span className="management-tag">{AUTO_TYPE_LABELS[row.auto_typeA]}</span>
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="mgmt-input mgmt-input--small"
                    value={editValues.auto_pourcentA}
                    onChange={(e) => setField('auto_pourcentA', e.target.value)}
                  />
                ) : (
                  `${row.auto_pourcentA} %`
                )}
              </td>
              <td>
                <div className="marges-price">
                  <span className="marges-price__sell">{formatEuro(row.prixV)}</span>
                  <span className="marges-price__buy">{formatEuro(row.prixA)}</span>
                </div>
              </td>
              <td>
                {isEditing ? (
                  <div className="management-actions">
                    <button
                      type="button"
                      className="mgmt-btn mgmt-btn--primary mgmt-btn--small"
                      disabled={isSaving}
                      onClick={() =>
                        saveEdit(
                          {
                            id_achator: row.id_achator,
                            auto_typeV: Number(editValues.auto_typeV),
                            auto_pourcentV: Number(editValues.auto_pourcentV),
                            auto_typeA: Number(editValues.auto_typeA),
                            auto_pourcentA: Number(editValues.auto_pourcentA),
                          },
                          `Marges "${row.label}" mises à jour pour ${agenceLabel}.`
                        )
                      }
                    >
                      {isSaving ? '…' : 'Enregistrer'}
                    </button>
                    <button type="button" className="mgmt-btn mgmt-btn--ghost mgmt-btn--small" onClick={cancelEdit}>
                      Annuler
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="mgmt-btn mgmt-btn--outline mgmt-btn--small"
                    onClick={() =>
                      startEdit(key, {
                        auto_typeV: row.auto_typeV,
                        auto_pourcentV: row.auto_pourcentV,
                        auto_typeA: row.auto_typeA,
                        auto_pourcentA: row.auto_pourcentA,
                      })
                    }
                  >
                    Modifier
                  </button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  const renderTitrages = () => (
    <table className="management-table">
      <thead>
        <tr>
          <th>Métal</th>
          <th>Titrage</th>
          <th>Titre (‰)</th>
          <th>Commission %</th>
          <th>Statut</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {filteredRows.map((row) => {
          const key = `tit-${row.id_titrage}`;
          const isEditing = editKey === key;
          return (
            <tr key={key}>
              <td>
                <span className={`management-tag ${row.id_metal === 1 ? 'management-tag--warning' : ''}`}>
                  {row.id_metal === 1 ? '🟡 Or' : '⚪ Argent'}
                </span>
              </td>
              <td><strong>{row.label}</strong></td>
              <td>{row.titre}</td>
              <td>
                {isEditing ? (
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    className="mgmt-input mgmt-input--small"
                    value={editValues.commission}
                    onChange={(e) => setField('commission', e.target.value)}
                  />
                ) : (
                  `${row.commission} %`
                )}
              </td>
              <td>
                <span className={`management-tag ${row.status ? 'management-tag--success' : 'management-tag--warning'}`}>
                  {row.status ? 'Actif' : 'Inactif'}
                </span>
              </td>
              <td>
                {isEditing ? (
                  <div className="management-actions">
                    <button
                      type="button"
                      className="mgmt-btn mgmt-btn--primary mgmt-btn--small"
                      disabled={isSaving}
                      onClick={() =>
                        saveEdit(
                          {
                            id_titrage: row.id_titrage,
                            commission: Number(editValues.commission),
                          },
                          `Commission ${row.label} (${row.id_metal === 1 ? 'Or' : 'Argent'}) mise à jour pour ${agenceLabel}.`
                        )
                      }
                    >
                      {isSaving ? '…' : 'Enregistrer'}
                    </button>
                    <button type="button" className="mgmt-btn mgmt-btn--ghost mgmt-btn--small" onClick={cancelEdit}>
                      Annuler
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="mgmt-btn mgmt-btn--outline mgmt-btn--small"
                    onClick={() => startEdit(key, { commission: row.commission })}
                  >
                    Modifier
                  </button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  return (
    <div className="management-page">
      <div className="management-page__inner">
        <header className="management-header glass-panel">
          <div className="management-header__content">
            <h1 className="management-title">
              <span className="management-title__icon">💹</span>
              Marges par agence
            </h1>
            <p className="management-subtitle">
              Paramétrez les marges devises, produits et rachat or au carat de chaque agence.
              Les modifications sont appliquées immédiatement sur coursdelorendirect.
            </p>
            <div className="management-meta">
              <span className="management-badge">{agences.length} agences</span>
              {agenceLabel && <span className="management-badge">Agence : {agenceLabel}</span>}
            </div>
          </div>
          <div className="management-header__actions">
            <select
              className="mgmt-input"
              value={idMag}
              onChange={(e) => setIdMag(e.target.value)}
              aria-label="Sélectionner une agence"
            >
              <option value="">— Choisir une agence —</option>
              {agences.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.label} (#{a.id})
                </option>
              ))}
            </select>
          </div>
        </header>

        {error && (
          <div className="management-alert error">
            <span>⚠️</span>
            {error}
          </div>
        )}
        {feedback && (
          <div className="management-alert success">
            <span>✅</span>
            {feedback}
          </div>
        )}

        <section className="management-section glass-panel">
          <div className="management-section__head">
            <div className="management-actions" role="tablist" aria-label="Type de marges">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  className={`mgmt-btn ${activeTab === tab.id ? 'mgmt-btn--primary' : 'mgmt-btn--outline'}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            {idMag && rows.length > 0 && (
              <div className="marges-filters">
                <input
                  type="text"
                  className="mgmt-input"
                  placeholder="🔍 Filtrer par nom ou code…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {activeTab === 'produits' && apiProducts && (
                  <label className="management-checkbox marges-filters__toggle">
                    <input
                      type="checkbox"
                      checked={onlyApi}
                      onChange={(e) => setOnlyApi(e.target.checked)}
                    />
                    Produits API uniquement ({apiProductsCount})
                  </label>
                )}
              </div>
            )}
          </div>

          {!idMag ? (
            <div className="management-empty-state">
              <span className="management-empty-state__icon">🏢</span>
              <span className="management-empty-state__title">Sélectionnez une agence</span>
              <span className="management-empty-state__subtitle">
                Choisissez une agence dans le sélecteur ci-dessus pour afficher et modifier ses marges.
              </span>
            </div>
          ) : isLoading ? (
            <div className="management-empty-state">
              <span className="management-empty-state__icon">⏳</span>
              <span className="management-empty-state__title">Chargement…</span>
            </div>
          ) : filteredRows.length === 0 ? (
            <div className="management-empty-state">
              <span className="management-empty-state__icon">📭</span>
              <span className="management-empty-state__title">Aucune ligne</span>
              <span className="management-empty-state__subtitle">
                {search
                  ? 'Aucun résultat pour ce filtre.'
                  : 'Cette agence n\'a pas de paramétrage pour ce type de marge.'}
              </span>
            </div>
          ) : (
            <div className="management-table-scroll">
              {activeTab === 'devises' && renderDevises()}
              {activeTab === 'produits' && renderProduits()}
              {activeTab === 'titrages' && renderTitrages()}
            </div>
          )}

          {idMag && !isLoading && rows.length > 0 && (
            <p className="management-section__subtitle" style={{ marginTop: '1rem' }}>
              {filteredRows.length} ligne{filteredRows.length > 1 ? 's' : ''} affichée{filteredRows.length > 1 ? 's' : ''}
              {search && ` (sur ${rows.length})`}
            </p>
          )}
        </section>
      </div>
    </div>
  );
};

export default Marges;
